"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BillingService } from "@/services/BillingService";
import type { BillingDashboardOutput } from "@/interfaces/billing";
import { numberToMoney } from "@/util/moneyConversion";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Banknote,
  CreditCard,
  Wallet,
  QrCode,
  CircleDollarSign,
} from "lucide-react";
import { BaseComponentProps } from "@/interfaces/dashboard";

export const ReceiptDetailsCard: React.FC<BaseComponentProps> = ({
  monthYear,
  isLoading = false,
}) => {
  const [data, setData] = useState<BillingDashboardOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const service = new BillingService();
        const response = await service.getDashboard({ monthYear });
        setData(response);
      } catch (err) {
        console.error("Failed to fetch receipt details:", err);
        setError("Falha ao carregar detalhes de recebimento");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [monthYear]);

  if (isLoading || loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Detalhes de Recebimentos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={`skeleton-${i}`} className="space-y-2">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <Skeleton className="h-2 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Detalhes de Recebimentos</CardTitle>
        </CardHeader>
        <CardContent className="text-red-500">{error}</CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const paymentMethods = [
    {
      name: "Dinheiro",
      value: data.cashReceived,
      icon: <Banknote className="h-5 w-5" />,
      color: "bg-green-500",
      textColor: "text-green-600",
    },
    {
      name: "PIX",
      value: data.pixReceived,
      icon: <QrCode className="h-5 w-5" />,
      color: "bg-blue-500",
      textColor: "text-blue-600",
    },
    {
      name: "Cartão de Crédito",
      value: data.creditCardReceived,
      icon: <CreditCard className="h-5 w-5" />,
      color: "bg-purple-500",
      textColor: "text-purple-600",
    },
    {
      name: "Cartão de Débito",
      value: data.debitCardReceived,
      icon: <Wallet className="h-5 w-5" />,
      color: "bg-orange-500",
      textColor: "text-orange-500",
    },
    {
      name: "Zelle",
      value: data.zelleReceived,
      icon: <CircleDollarSign className="h-5 w-5" />,
      color: "bg-orange-500",
      textColor: "text-orange-500",
    },
  ];

  // Calcular o total para as porcentagens
  const total = paymentMethods.reduce((sum, method) => sum + method.value, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Wallet className="h-5 w-5 mr-2 text-orange-500" />
          Detalhes de Recebimentos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {paymentMethods.map((method, index) => {
            const percentage = total > 0 ? (method.value / total) * 100 : 0;

            return (
              <motion.div
                key={method.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="space-y-2"
              >
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <div
                      className={`${method.color} p-1.5 rounded-full text-white mr-2`}
                    >
                      {method.icon}
                    </div>
                    <span>{method.name}</span>
                  </div>
                  <span className={`font-semibold ${method.textColor}`}>
                    {numberToMoney(method.value)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Progress value={percentage} className="h-2" />
                  <span className="text-xs text-muted-foreground w-12 text-right">
                    {percentage.toFixed(1)}%
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
